import { queryOptions, useQuery } from "@tanstack/react-query";
import { getLinearPriceInfo } from "~/domains/linear/api/GetLinearPriceInfoSingle";
import { getLinearAvailabilityInfo } from "~/domains/linear/api/GetLinearAvailabilityInfoSingle";
interface LinearPnAConfiguration {
  productCode: string;
  configId: string;
  priceFingerprint: string | null;
  availabilityFingerprint: string | null;
}

interface LinearPnAQueryInput {
  configuration: LinearPnAConfiguration;
  quantity: number;
  priceReference: string;
  enabled?: boolean;
}

const PNA_REQUEST_QUANTITY = 1;

export const linearPriceQueryOptions = ({ configuration, priceReference, enabled = true }: LinearPnAQueryInput) => {
  return queryOptions({
    queryKey: [
      "linear-config-pna",
      "price",
      configuration.productCode,
      configuration.configId,
      configuration.priceFingerprint,
      priceReference,
    ] as const,
    // The shared six-slot PnA lane (task 0042 A02/A03) sits below these fetchers; threading the
    // TanStack signal makes queued/running V2 PnA work abortable like every other PnA path.
    queryFn: ({ signal }) =>
      getLinearPriceInfo(
        configuration.productCode,
        configuration.configId,
        priceReference,
        PNA_REQUEST_QUANTITY,
        undefined,
        signal,
      ),
    enabled: enabled && configuration.priceFingerprint !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const linearAvailabilityQueryOptions = ({
  configuration,
  priceReference,
  enabled = true,
}: LinearPnAQueryInput) => {
  return queryOptions({
    queryKey: [
      "linear-config-pna",
      "availability",
      configuration.productCode,
      configuration.configId,
      configuration.availabilityFingerprint,
      priceReference,
    ] as const,
    queryFn: ({ signal }) =>
      getLinearAvailabilityInfo(
        configuration.productCode,
        configuration.configId,
        priceReference,
        PNA_REQUEST_QUANTITY,
        undefined,
        signal,
      ),
    enabled: enabled && configuration.availabilityFingerprint !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useLinearPriceQuery = (input: LinearPnAQueryInput) => useQuery(linearPriceQueryOptions(input));

export const useLinearAvailabilityQuery = (input: LinearPnAQueryInput) =>
  useQuery(linearAvailabilityQueryOptions(input));
